import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { needsBillingRecovery } from '../lib/billingRecovery';
import { trackEvent } from '../lib/analytics';

function recoveryKey() {
  const { user } = useAuthStore.getState();
  const { subscription } = useSubscriptionStore.getState();
  if (!user || subscription?.user_id !== user.id || !needsBillingRecovery(subscription)) return '';
  return `${user.id}:${subscription.stripe_subscription_id}:${subscription.status}:${subscription.current_period_end}`;
}

export function isCurrentUserBillingRecoveryRequired() {
  return !!recoveryKey();
}

/** Portal navigation is scoped to this visible surface and authenticated owner. */
export function useBillingRecoveryAction(enabled: boolean, source: string) {
  const user = useAuthStore((s) => s.user);
  const authLoading = useAuthStore((s) => s.isLoading);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const subscriptionLoading = useSubscriptionStore((s) => s.isLoading);
  const storedPlan = useSubscriptionStore((s) => s.plan);
  const createPortalSession = useSubscriptionStore((s) => s.createPortalSession);
  const owned = !!user && subscription?.user_id === user.id;
  const needsRecovery = owned && needsBillingRecovery(subscription);
  const key = needsRecovery
    ? `${user!.id}:${subscription!.stripe_subscription_id}:${subscription!.status}:${subscription!.current_period_end}`
    : '';
  // A loading or previous account's record is not this account's current plan.
  const currentPlan = authLoading || subscriptionLoading || (!!subscription && !owned)
    ? null
    : owned ? storedPlan : 'free';
  const mounted = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const inFlight = useRef<{ key: string } | null>(null);
  const [state, setState] = useState({ key: '', busy: false, error: false });

  const cancel = useCallback(() => {
    inFlight.current = null;
    if (mounted.current) setState({ key: '', busy: false, error: false });
  }, []);

  useEffect(() => {
    mounted.current = true;
    // Synchronous store subscriptions also catch sign-out/switch-away-and-back
    // before React renders again or the pending portal promise settles.
    const invalidate = () => {
      if (inFlight.current && recoveryKey() !== inFlight.current.key) cancel();
    };
    const unsubscribeAuth = useAuthStore.subscribe(invalidate);
    const unsubscribeSubscription = useSubscriptionStore.subscribe(invalidate);
    // Back/Forward may restore this component from bfcache without mounting it
    // again. Release the navigation lock only on an actual restored page.
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) cancel();
    };
    window.addEventListener('pageshow', restore);
    return () => {
      mounted.current = false;
      inFlight.current = null;
      unsubscribeAuth();
      unsubscribeSubscription();
      window.removeEventListener('pageshow', restore);
    };
  }, [cancel]);

  useEffect(() => { cancel(); }, [enabled, key, cancel]);

  const pause = useCallback(() => {
    enabledRef.current = false;
    cancel();
  }, [cancel]);

  const openBilling = useCallback(async () => {
    if (!mounted.current || !enabledRef.current || !key || recoveryKey() !== key || inFlight.current) return;
    // The ref locks synchronously; React's disabled state alone permits two
    // clicks in the same tick to create two sessions.
    const request = { key };
    inFlight.current = request;
    setState({ key, busy: true, error: false });
    const status = useSubscriptionStore.getState().subscription?.status;
    const isCurrent = () => mounted.current && enabledRef.current
      && inFlight.current === request && recoveryKey() === key;
    trackEvent('billing_recovery_click', { status, source });
    try {
      const url = await createPortalSession();
      if (!isCurrent()) return;
      if (!url) throw new Error('Billing unavailable');
      trackEvent('billing_recovery_opened', { status, source });
      window.location.assign(url);
      // Keep the lock while navigation is pending. Close/unmount or an owner
      // change cancels it; failures below release it for an explicit retry.
    } catch {
      if (!isCurrent()) return;
      inFlight.current = null;
      setState({ key, busy: false, error: true });
      trackEvent('billing_recovery_error', { status, source });
    }
  }, [key, createPortalSession, source]);

  return {
    needsRecovery,
    currentPlan,
    openBilling,
    cancel,
    pause,
    enabled,
    busy: enabled && state.key === key && state.busy,
    error: enabled && state.key === key && state.error,
  };
}
